import React from "react";
import Layout from "../components/layout/LandingPageLayout";
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TimelineIcon from "@mui/icons-material/Timeline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {Link} from "react-router-dom";

const TaskMaangerInfo = () => {
  const theme = useTheme();

  const features = [
    {
      title: "Task Creation",
      description:
        "Create and assign tasks effortlessly to stay organized and productive.",
      icon: (
        <AssignmentTurnedInIcon
          sx={{ fontSize: 50, color: theme.palette.primary.main }}
        />
      ),
    },
    {
      title: "Progress Tracking",
      description:
        "Track ongoing tasks with real-time updates and stay on top of deadlines.",
      icon: (
        <TimelineIcon
          sx={{ fontSize: 50, color: theme.palette.success.main }}
        />
      ),
    },
    {
      title: "Direct Calendar Access",
      description:
        "Seamlessly integrate with your calendar for reminders and scheduling.",
      icon: (
        <CalendarMonthIcon
          sx={{ fontSize: 50, color: theme.palette.info.main }}
        />
      ),
    },
  ];

  return (
    <Layout>
      <Box
        sx={{
          py: 8,
          px: 3,
          textAlign: "center",
          background: theme.palette.background.default,
        }}
      >
        {/* Heading */}
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Smarter Task Management
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: "700px", mx: "auto", mb: 6 }}
        >
          Manage tasks, monitor progress, and sync directly with your calendar —
          everything you need to stay productive.
        </Typography>

        {/* Features Grid */}
        <Grid container spacing={4} justifyContent="center" mb={6}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  boxShadow: 3,
                  borderRadius: 3,
                  height: "100%",
                  transition: "0.3s",
                  "&:hover": { transform: "translateY(-5px)", boxShadow: 6 },
                }}
              >
                <CardContent sx={{ textAlign: "center" }}>
                  {feature.icon}
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Dummy Task Manager Preview */}
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, textAlign: "left" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Task List (Preview)
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="✔ Prepare Financial Report"
                      secondary="Due: Today"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="📊 Review Expense Breakdown"
                      secondary="Due: Tomorrow"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="🗂 Upload Balance Sheet"
                      secondary="Due: Friday"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, textAlign: "center" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Calendar Access (Preview)
                </Typography>
                <Box
                  sx={{
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    p: 2,
                    fontSize: "0.9rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  <Typography fontWeight="bold" color="primary.main">
                    September 2025
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography>📌 30th – Client Meeting</Typography>
                  <Typography>📌 2nd – Expense Review</Typography>
                  <Typography>📌 5th – File Submission</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* CTA */}
        <Box mt={8}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Ready to manage your tasks efficiently?
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Assign, track, and sync with your calendar — all in one place.
          </Typography>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            size="large"
            sx={{
              borderRadius: "30px",
              px: 5,
              py: 1.8,
              fontSize: "1rem",
              textTransform: "none",
              boxShadow: 4,
            }}
          >
            Try Task Manager Now
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};

export default TaskMaangerInfo;
